import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

/**
 * Cabeçalho de tabela ordenável.
 *
 * Padrão extraído da página Recorrentes para uniformizar o comportamento de
 * ordenação em todas as tabelas do sistema. Cada `<th>` é um botão que alterna
 * o sentido (asc/desc) ao ser clicado; clicar em outra coluna reinicia em asc.
 *
 * Uso:
 *
 *   const [sort, setSort] = useState<SortState<MinhaColuna>>({ column: 'nome', dir: 'asc' });
 *   const toggleSort = makeToggleSort(setSort);
 *   ...
 *   <SortHeader column="nome" label="Nome" sort={sort} onClick={toggleSort} />
 */

export type SortDir = 'asc' | 'desc';

export interface SortState<C extends string> {
  column: C;
  dir: SortDir;
}

export interface SortHeaderProps<C extends string> {
  column: C;
  label: string;
  sort: SortState<C>;
  onClick: (col: C) => void;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export function SortHeader<C extends string>({
  column,
  label,
  sort,
  onClick,
  align = 'left',
  className = '',
}: SortHeaderProps<C>) {
  const isActive = sort.column === column;
  const Icon = !isActive ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  const justify =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const textAlign =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  return (
    <th className={`px-3 py-2 font-medium select-none ${textAlign} ${className}`}>
      <button
        type="button"
        onClick={() => onClick(column)}
        className={`inline-flex items-center gap-1 ${justify} w-full hover:text-foreground transition-colors ${isActive ? 'text-foreground' : ''}`}
        aria-sort={isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        <Icon size={12} className={isActive ? 'opacity-100' : 'opacity-40'} />
      </button>
    </th>
  );
}

/**
 * Helper que cria um `toggleSort` padronizado: clicar na mesma coluna inverte
 * o sentido; clicar em outra coluna recomeça em ascendente.
 */
export function makeToggleSort<C extends string>(
  setSort: (updater: (cur: SortState<C>) => SortState<C>) => void,
) {
  return (column: C) => {
    setSort((cur) =>
      cur.column === column
        ? { column, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: 'asc' },
    );
  };
}

/**
 * Comparadores genéricos reutilizáveis. Sempre retornam `negativo | 0 |
 * positivo` para serem multiplicados pelo `factor` (asc=1, desc=-1).
 */
export const cmp = {
  text(a: string | null | undefined, b: string | null | undefined): number {
    return (a ?? '').localeCompare(b ?? '', 'pt-BR');
  },
  number(a: number | string | null | undefined, b: number | string | null | undefined): number {
    return Number(a ?? 0) - Number(b ?? 0);
  },
  date(a: string | Date | null | undefined, b: string | Date | null | undefined): number {
    const av = a ? new Date(a).getTime() : 0;
    const bv = b ? new Date(b).getTime() : 0;
    return av - bv;
  },
  bool(a: boolean | null | undefined, b: boolean | null | undefined): number {
    return Number(Boolean(a)) - Number(Boolean(b));
  },
};
